/**
 * View Configuration Menu - save, revert, reset, import/export view settings.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  Checkbox,
  Select,
  Icon,
  useTheme,
  spacing,
  fontSize,
} from '@fti/design-system';
import type { ViewConfigMenuProps } from './contracts';
import { Popover } from './Popover';
import { ConfirmModal } from '../shared/ConfirmModal';

export function ViewConfigMenu({
  isDirty,
  hasSaved,
  activeFilterCount,
  newColumns,
  onNewColumnsChange,
  onSave,
  onRevert,
  onReset,
  onExport,
  onImport,
}: ViewConfigMenuProps) {
  const { theme } = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [includeFilters, setIncludeFilters] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSave = () => {
    const msg = onSave(includeFilters);
    setMessage({ text: msg, type: 'success' });
    handleClose();
  };

  const handleRevert = () => {
    onRevert();
    handleClose();
  };

  const handleResetClick = () => {
    setShowResetConfirm(true);
  };

  const handleResetConfirm = () => {
    onReset();
    setShowResetConfirm(false);
    handleClose();
  };

  const handleExport = () => {
    onExport();
    handleClose();
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await onImport(file);
      setMessage({ text: 'Imported and saved as your default.', type: 'success' });
      handleClose();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Import failed.';
      setMessage({ text: errorMsg, type: 'error' });
    } finally {
      // Reset input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  const newColumnsOptions = [
    { value: 'show', label: 'Show at end' },
    { value: 'hide', label: 'Hide' },
  ];

  return (
    <>
      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: spacing.xs }}>
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Icon name="settings" size={14} />}
          onClick={handleOpenMenu}
        >
          View
          {isDirty && (
            <span
              style={{
                marginLeft: spacing.xs,
                fontSize: '0.625rem',
                lineHeight: 1.4,
                background: theme.colors.semantic.warning.light,
                color: theme.colors.semantic.warning.dark,
                padding: '0 6px',
                borderRadius: '8px',
                whiteSpace: 'nowrap',
              }}
            >
              Modified
            </span>
          )}
        </Button>
      </div>

      {message && (
        <div
          style={{
            position: 'fixed',
            top: spacing.lg,
            right: spacing.lg,
            zIndex: 10000,
            background: message.type === 'success'
              ? theme.colors.semantic.success.default
              : theme.colors.semantic.error.default,
            color: '#fff',
            padding: `${spacing.sm} ${spacing.md}`,
            borderRadius: '4px',
            fontSize: fontSize.sm,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          {message.text}
        </div>
      )}

      <Popover
        anchor={anchorEl}
        open={open}
        onClose={handleClose}
        placement="bottom-end"
        width={300}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md, padding: spacing.md }}>
          {/* Save section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            <Checkbox
              checked={includeFilters}
              onChange={(e) => setIncludeFilters(e.target.checked)}
              disabled={activeFilterCount === 0}
              label={`Include current filters (${activeFilterCount})`}
            />
            <Button
              variant="solid"
              size="sm"
              fullWidth
              onClick={handleSave}
            >
              Save as my default view
            </Button>
            <p style={{ margin: 0, fontSize: fontSize.xs, color: mutedColor }}>
              Your default is applied automatically to every preview on this browser.
            </p>
          </div>

          {/* Revert / Reset */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              onClick={handleRevert}
              disabled={!hasSaved || !isDirty}
            >
              Revert to my default
            </Button>
            <Button
              variant="ghost"
              color="error"
              size="sm"
              fullWidth
              onClick={handleResetClick}
              disabled={!hasSaved}
            >
              Reset to built-in default
            </Button>
          </div>

          {/* New columns policy */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
            <label
              htmlFor="new-columns-select"
              style={{ fontSize: fontSize.xs, color: textColor }}
            >
              New columns:
            </label>
            <Select
              size="sm"
              options={newColumnsOptions}
              value={newColumns}
              onChange={(v) => onNewColumnsChange(v as 'show' | 'hide')}
            />
            <p style={{ margin: 0, fontSize: fontSize.xs, color: mutedColor }}>
              Applies to fields the provider adds later.
            </p>
          </div>

          {/* Divider */}
          <div
            style={{
              height: 1,
              background: theme.colors.border.default,
            }}
          />

          {/* Import / Export */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              leftIcon={<Icon name="download" size={14} />}
              onClick={handleExport}
            >
              Export view settings
            </Button>
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              leftIcon={<Icon name="upload" size={14} />}
              onClick={handleImportClick}
            >
              Import view settings
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      </Popover>

      <ConfirmModal
        isOpen={showResetConfirm}
        title="Reset to built-in default"
        message="Remove your saved default view? The built-in layout will be used."
        confirmLabel="Reset"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleResetConfirm}
        onCancel={() => setShowResetConfirm(false)}
      />
    </>
  );
}
